import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import BookNowPreviewModal from "../components/BookNowPreviewModal";
import { useAuth } from "../components/authContext";
import ArrowBackIos from "@mui/icons-material/ArrowBackIos";
import "./Preview.css";
import { useRequireListing } from "../hooks/useRequireListing";

const API_BASE_URL = process.env.REACT_APP_BASE_URL;

const formatMoney = (n) => {
    const x = Number.isFinite(+n) ? +n : 0;
    return `$${x.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

function cityStateFromAddress(addr = "") {
    const parts = String(addr).split(",").map(s => s.trim());
    if (parts.length >= 2) return `${parts[0]}, ${parts[1]}`;
    return addr;
}


export default function Preview() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { checking: checkingListing, allowed: hasListing } = useRequireListing();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [data, setData] = useState(null);

    // Simple booking modal state
    const [booking, setBooking] = useState(null);

    // Guard: must be signed in to view preview
    useEffect(() => {
        if (user === null) {
            navigate("/login", { replace: true, state: { from: "/preview" } });
        }
    }, [user, navigate]);

    // Load preview data
    useEffect(() => {
        let alive = true;
        (async () => {
            if (!user || !hasListing) return;
            try {
                setLoading(true);
                const token = await user.getIdToken();
                const res = await fetch(`${API_BASE_URL}/api/my/preview`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const json = await res.json();
                if (!res.ok) throw new Error(json?.error || "Failed to load preview.");
                if (!alive) return;

                setData(json);
            } catch (e) {
                if (alive) setError(e.message);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [user, hasListing]);

    const name = data?.displayName || data?.username || "Your Page";
    const avatar = data?.profilePictureUrl || "";
    const location = useMemo(() => cityStateFromAddress(data?.address), [data]);

    const onBack = () => navigate("/dashboard");

    if (!user || checkingListing) {
        return <div className="pv-container"><div className="pv-card">Loading…</div></div>;
    }
    if (loading) {
        return <div className="pv-container"><div className="pv-card">Loading preview…</div></div>;
    }
    if (error) {
        return (
            <div className="pv-container">
                <div className="pv-card">
                    <div className="pv-error">{error}</div>
                    <button className="pv-btn" onClick={() => window.location.reload()}>Retry</button>
                </div>
            </div>
        );
    }

    return (

        <div className="pv-container">
            <BookNowPreviewModal
                open={booking}
                onClose={() => setBooking(null)}
                creatorName={name}
                avatarUrl={avatar}
            />
            <div className="pv-card">
                <div className="pv-hero-plate" aria-hidden="true" />
                {/* Back */}
                <button className="pv-back" onClick={onBack} aria-label="Back to My Listing">
                    <ArrowBackIos fontSize="small" />
                    <span>Back</span>
                </button>

                {/* Header */}
                <div className="pv-head">
                    <div className="pv-avatar-wrap">
                        {avatar ? (
                            <img className="pv-avatar" src={avatar} alt={`${name} avatar`} />
                        ) : (
                            <div className="pv-avatar pv-avatar-fallback">🙂</div>
                        )}
                    </div>
                    <h1 className="pv-name">{name}</h1>
                    {location && <div className="pv-location">{location}</div>}
                    {data?.bio && <p className="pv-bio">{data.bio}</p>}
                </div>

                {/* Activities */}
                <div className="pv-section-title">Activities Offered</div>
                <div className="pv-acts">
                    {(data?.activities || []).map((a) => (
                        <div className="pv-act" key={a.id}>
                            <div className="pv-act-main">
                                <div className="pv-act-title">{a.title}</div>
                                <div className="pv-act-sub">{formatMoney(a.price)} • {a.durationLabel}</div>
                                {a.blurb && <div className="pv-act-blurb">{a.blurb}</div>}
                            </div>
                            <button className="pv-book-btn" onClick={() => setBooking(a)}>
                                Book Now
                            </button>
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <div className="pv-footer">
                    <img src="/logo192.png" alt="ActivityBuddy" className="pv-logo" />
                    <div className="pv-powered">Powered by <strong>ActivityBuddy</strong></div>
                </div>
            </div>
        </div>
    );
}
